import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useSurveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/surveys/all${params}`);
      if (response.data.success) {
        setSurveys(response.data.data.surveys);
      }
    } catch (error) {
      console.error('Error loading surveys:', error);
      toast({
        title: "Error",
        description: "Failed to load surveys",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surveys/stats`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  const deleteSurvey = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;
    
    try {
      const response = await axios.delete(`${API}/surveys/${surveyId}`);
      if (response.data.success) {
        toast({
          title: "Survey Deleted",
          description: "Survey has been deleted successfully",
        });
        loadSurveys();
        loadStats();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete survey",
        variant: "destructive",
      });
    }
  };

  const updateSurveyStatus = async (surveyId, newStatus) => {
    try {
      const response = await axios.patch(`${API}/surveys/${surveyId}/status?status=${newStatus}`);
      if (response.data.success) {
        toast({
          title: "Status Updated",
          description: `Survey status changed to ${newStatus}`,
        });
        loadSurveys();
        loadStats();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update survey status",
        variant: "destructive",
      });
    }
  };

  const createSurvey = async (formData) => {
    // Helper: convert empty/null/undefined to null, otherwise parse as float
    const toNullableFloat = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const parsed = parseFloat(v);
      return isNaN(parsed) ? null : parsed;
    };

    const cleanedOpenings = formData.openings.map(opening => ({
      ...opening,
      opening_reference: opening.opening_reference || null,
      room_location: opening.room_location || null,
      layout: opening.layout || null,
      width: toNullableFloat(opening.width),
      height: toNullableFloat(opening.height),
      frame_size: toNullableFloat(opening.frame_size),
      midrail: toNullableFloat(opening.midrail),
      split_height: toNullableFloat(opening.split_height),
      // Clean bay_posts: convert empty measurement/angle to null
      bay_posts: (opening.bay_posts || []).map(bp => ({
        measurement: toNullableFloat(bp.measurement),
        angle: toNullableFloat(bp.angle),
      })),
      // Clean t_posts: convert empty strings to null
      t_posts: (opening.t_posts || []).filter(v => v !== '' && v !== null && v !== undefined).map(v => parseFloat(v)).filter(v => !isNaN(v)),
      notes: opening.notes || null,
    }));

    const cleanedData = {
      ...formData,
      customer_email: formData.customer_email || null,
      customer_phone: formData.customer_phone || null,
      customer_address: formData.customer_address || null,
      customer_postcode: formData.customer_postcode || null,
      survey_date: formData.survey_date || null,
      surveyor_name: formData.surveyor_name || null,
      property_notes: formData.property_notes || null,
      general_notes: formData.general_notes || null,
      access_notes: formData.access_notes || null,
      installation_notes: formData.installation_notes || null,
      openings: cleanedOpenings,
    };

    const response = await axios.post(`${API}/surveys/create`, cleanedData);
    return response.data;
  };

  useEffect(() => {
    loadSurveys();
    loadStats();
  }, [loadSurveys, loadStats]);

  return {
    surveys,
    loading,
    stats,
    statusFilter,
    setStatusFilter,
    loadSurveys,
    loadStats,
    deleteSurvey,
    updateSurveyStatus,
    createSurvey
  };
};

// Utility functions
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
};

export const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'draft': return 'bg-gray-100 text-gray-800';
    case 'in_progress': return 'bg-blue-100 text-blue-800';
    case 'completed': return 'bg-green-100 text-green-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export const getWindowTypeLabel = (windowType) => {
  const types = {
    'flat_window': 'Flat Window',
    'angled_bay': 'Angled Bay',
    'corner_bay': 'Corner Bay'
  };
  return types[windowType] || windowType;
};
